import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';
import { devicesQueryKey, simsQueryKey } from '@/features/inventory/useInventory';
import type { Motion } from '@/features/live/useAdminLive';
import type { CheckResult } from '@/features/provisioning/useProvisioning';

/**
 * THE BENCH (slice 18f).
 *
 * Mahad: *"I want to see the device up and reporting before I assign to
 * vehicle… I prepare the device, when I sell to tenant and agree with plan, the
 * installation should happen because I already tested the device earlier and saw
 * it under commissioning or global view."*
 *
 * Types mirror `CommissionedDeviceZ` / `CommissioningResponseZ` /
 * `DeviceVerifyResponseZ` in `api/src/modules/admin/routes.ts` (seam S1).
 */

export interface CommissionedDevice {
  deviceId: string;
  imei: string;
  model: string | null;
  status: 'in_service' | 'retired' | 'faulty';
  traccarDeviceId: number;
  commissionedAt: string | null;
  simIccid: string | null;
  simMsisdn: string | null;
  simCarrier: string | null;
  lat: number | null;
  lon: number | null;
  speedKmh: number | null;
  course: number | null;
  /** The SAME `deriveMotion` the customer map uses — never a second opinion. */
  motion: Motion;
  fixTime: string | null;
  /**
   * Slice 14a — whether this tracker still answers to the shared credential.
   *
   * 🔴 THERE IS NO SECRET HERE, AND THE SCREEN NEVER NEEDS ONE. Derived by the
   * API's own `deriveState`, so the operator's list and the harden endpoint
   * cannot disagree about what "hardened" means.
   *
   * `no_command_channel` is an osmand phone: it can NEVER be hardened and is not
   * a warning. Rendering it the same as `unhardened` would put a permanent
   * red mark on two of five devices and teach the operator to ignore the column.
   */
  hardening:
    | 'no_command_channel'
    | 'not_commissioned'
    | 'unhardened'
    | 'secret_set'
    | 'hardened';
  /**
   * SLICE 14e — what a command physically does on this device. `null` is the
   * SAFE state ("nobody recorded it"), not a missing value: the full speed and
   * freshness guard applies.
   */
  interlockType: 'starter' | 'ignition' | 'fuel' | null;
}

export interface CommissioningResponse {
  devices: CommissionedDevice[];
  serverTime: string;
  /**
   * 🔴 TRUE MEANS REDIS COULD NOT BE READ — "we are blind", NOT "nothing is
   * reporting". The API measured that a degraded read yields every device with a
   * null position and `motion: 'offline'`, which is indistinguishable from a
   * bench of dead trackers — the one thing this screen exists to tell apart. The
   * screen must never render "all stopped" while blind.
   */
  degraded: boolean;
  /** The offline threshold these `motion` values were derived against. */
  offlineThresholdSec: number;
}

export interface DeviceVerifyResponse {
  deviceId: string;
  traccarDeviceId: number;
  checks: CheckResult[];
  allPassed: boolean;
}

export const commissioningQueryKey = ['admin', 'commissioning'] as const;

/** A 4xx is a decision, not a hiccup. */
const retry4xxNever = (count: number, error: Error) =>
  !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2;

/**
 * `enabled` keeps a non-super-admin from firing a request that can only 403 —
 * `GET /admin/commissioning` carries IMEIs and is `requireSuperAdmin`.
 */
export function useCommissioning(enabled = true): UseQueryResult<CommissioningResponse> {
  return useQuery({
    queryKey: commissioningQueryKey,
    enabled,
    queryFn: ({ signal }) =>
      apiJson<CommissioningResponse>('/api/admin/commissioning', { signal }),
    // The bench is watched while a tracker warms up. Five seconds is what
    // `/live` polls at and a human cannot tell the difference from a socket.
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
    retry: retry4xxNever,
  });
}

export interface CommissionResponse {
  deviceId: string;
  traccarDeviceId: number;
  commissionedAt: string;
  /** An earlier attempt crashed after Traccar created the device; this one
   * ADOPTED it rather than creating a second. Told, never silent. */
  adopted: boolean;
}

/**
 * Register a tracker with Traccar before any customer exists.
 *
 * 🔴 `apiJson`, NOT `apiVoid` — this route answers `201` with a body. The
 * opposite mistake (`apiJson` on a 204) is the 17x defect that turns a
 * SUCCESSFUL action into a visible `SyntaxError`; both directions are wrong and
 * the status code decides which helper is correct.
 */
export function useCommissionDevice(): UseMutationResult<
  CommissionResponse,
  Error,
  { deviceId: string; name?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, name }) =>
      apiJson<CommissionResponse>(`/api/admin/devices/${deviceId}/commission`, {
        method: 'POST',
        body: JSON.stringify(name ? { name } : {}),
      }),
    onSuccess: () => {
      // The device leaves "never registered" in the register AND appears on the
      // bench. Both lists are the same fact seen from two screens; invalidating
      // one would leave the other claiming the tracker was never commissioned.
      void qc.invalidateQueries({ queryKey: devicesQueryKey });
      void qc.invalidateQueries({ queryKey: simsQueryKey });
      void qc.invalidateQueries({ queryKey: commissioningQueryKey });
    },
  });
}

/**
 * The SAME three checks the go-live gate runs, against a tracker that has no
 * vehicle yet. `requireStaff` — it reports a position and a verdict, not an
 * IMEI.
 */
export function useDeviceChecks(): UseMutationResult<DeviceVerifyResponse, Error, string> {
  return useMutation({
    mutationFn: (deviceId: string) =>
      apiJson<DeviceVerifyResponse>(`/api/admin/devices/${deviceId}/checks`),
  });
}

/**
 * Ready to COMMISSION — the set the `/devices` action appears for.
 *
 * Mirrors `commissionDevice`'s five preconditions, which are re-checked
 * server-side before the Traccar call. It lives here once so the register screen
 * and any later caller cannot drift on what "ready to commission" means.
 *
 *   `traccarDeviceId === null` — not already commissioned or provisioned.
 *   `status === 'in_service'`  — retired and faulty are refused.
 *   `inStock`                  — no open vehicle assignment.
 *   `simId !== null`           — a tracker with no SIM can never report, so it
 *                                could never pass a check. An UNACTIVATED SIM
 *                                (ICCID, no MSISDN) is accepted deliberately.
 */
export function isReadyToCommission(d: {
  traccarDeviceId: number | null;
  status: string;
  inStock: boolean;
  simId: string | null;
}): boolean {
  return (
    d.traccarDeviceId === null && d.status === 'in_service' && d.inStock && d.simId !== null
  );
}
